import { toast } from 'sonner';
import { ListChecks } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import type { TfxDraftDTO } from '@/api/generated/model';
import { useApproveTfxDraft, useRejectTfxDraft } from '../api';
import { TfxStatusBadge } from './TfxStatusBadge';
import { TfxPreviewVideo } from './TfxPreviewVideo';
import { formatModifiedAt, packageLabel } from '../lib/display';

// Static review rubric — display-only helper text, nothing is persisted.
const REVIEW_RUBRIC: { label: string; hint: string }[] = [
  { label: 'Intent', hint: 'the effect does what its name/note promises' },
  { label: 'Legibility', hint: 'subject stays readable under the effect' },
  { label: 'Motion', hint: 'timing feels smooth, no stutter or popping' },
  { label: 'Artifacts', hint: 'no banding, clipping, or edge garbage' },
  { label: 'Identity', hint: 'fits the TipMi look; no IP/brand lookalikes' },
];

/**
 * One review-queue row (pattern: EffectPackCandidateCard). Approve/Reject each own their
 * mutation instance so `pending` is scoped to THIS card only.
 */
export function TfxReviewCard({ draft }: { draft: TfxDraftDTO }) {
  const confirm = useConfirm();
  const approveMutation = useApproveTfxDraft();
  const rejectMutation = useRejectTfxDraft();

  const id = draft.id;
  const label = packageLabel(draft.packageId, draft.version);
  const note = draft.note?.trim();
  const pending = approveMutation.isPending || rejectMutation.isPending;

  const onApprove = async () => {
    if (id == null) return;
    const { confirmed } = await confirm({
      title: 'Approve effect package',
      description: `Approve "${label}"? It will be published to the mobile CDN and appear in the app's effect catalog.`,
      confirmText: 'Approve',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await approveMutation.mutateAsync({ id });
      toast.success('Draft approved and queued for publish');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve draft');
    }
  };

  const onReject = async () => {
    if (id == null) return;
    // `requireReason` keeps Confirm disabled until a reason is typed — the API requires it.
    const { confirmed, reason } = await confirm({
      title: 'Reject effect package',
      description: `Reject "${label}"? The author will see your reason on their draft.`,
      confirmText: 'Reject',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed || !reason?.trim()) return;
    try {
      await rejectMutation.mutateAsync({ id, data: { reason: reason.trim() } });
      toast.success('Draft rejected');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject draft');
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-start gap-4">
          <TfxPreviewVideo url={draft.previewUrl} />

          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-mono text-sm font-semibold">{label}</p>
              <TfxStatusBadge status={draft.status ?? ''} />
              <span className="text-xs text-muted-foreground">iteration {draft.iteration ?? 0}</span>
            </div>

            <p className="text-xs text-muted-foreground">
              {draft.ownerEmail?.trim() || 'unknown author'} · modified{' '}
              {formatModifiedAt(draft.modifiedAt)}
            </p>

            {note && <p className="text-sm text-muted-foreground">{note}</p>}

            <div className="rounded-md border bg-muted/40 p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
                <ListChecks className="size-3.5" />
                Review rubric
              </p>
              <ul className="space-y-0.5 text-xs text-muted-foreground">
                {REVIEW_RUBRIC.map((item) => (
                  <li key={item.label}>
                    <span className="font-medium text-foreground/80">{item.label}</span> —{' '}
                    {item.hint}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button
            variant="destructive"
            size="sm"
            disabled={pending || id == null}
            onClick={() => void onReject()}
          >
            Reject
          </Button>
          <Button size="sm" disabled={pending || id == null} onClick={() => void onApprove()}>
            Approve
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
